import { CurationItem } from "../../types";
import { callCurationProxy } from "../backend/backendClient";
import {
    listLogger,
    IMAGE_SERVICE_ADMIN_BASE_URL,
    parseJsonOrThrow,
    readTextOrThrow,
    resolvePublisherId,
} from "./helpers";

/**
 * Options for creating an asset
 * @property {string} [publisherId] - Publisher ID to use for the asset. Defaults to the publisher ID from the authentication cookies.
 */
export interface CreateAssetOptions {
    publisherId?: string;
}

/**
 * Create an asset
 * @param parentId
 * @param title
 * @param listId
 * @param imagePackId
 * @param options
 * @returns {Promise<CurationItem>}
 */
export const createAsset = async (
    parentId: string,
    title: string,
    listId: string,
    imagePackId: string,
    options: CreateAssetOptions = {},
): Promise<CurationItem> => {
    if (!parentId || !title || !listId || !imagePackId) {
        throw new Error("createAsset requires a parentId, title, listId, and imagePackId.");
    }

    const validatedPublisherId = resolvePublisherId(options.publisherId);
    const imageUrl = `${IMAGE_SERVICE_ADMIN_BASE_URL}/api/v2/img/${encodeURIComponent(imagePackId)}`;

    const createBody = [
        {
            listId,
            title,
            contentType: "asset",
            documentType: "asset",
            metadata: {
                en_US: {
                    title,
                },
                default: {
                    "publisher-id": validatedPublisherId,
                },
            },
            imageUrl,
            imagePackId,
            parentId,
        },
    ];

    listLogger.debug("POST body:", JSON.stringify(createBody, null, 2));

    const createResponseText = await readTextOrThrow(
        await callCurationProxy({
            path: "/list/?index=0&copyImagePack=true",
            method: "POST",
            body: createBody,
        }),
        "Asset creation (POST)",
    );

    const creationData = parseJsonOrThrow<{ result?: CurationItem[] }>(
        createResponseText,
        "Asset creation",
    );
    const newAsset = creationData.result?.[0];

    if (!newAsset?.id) {
        throw new Error(
            `API Error: Asset creation succeeded but ID was not returned. Response: ${createResponseText}`,
        );
    }

    listLogger.debug("Response:", JSON.stringify(newAsset, null, 2));

    return newAsset;
};
